import { StepConfig } from '@/classes/StepConfig';
import envHelper from '@/helpers/envHelper';

const allSteps: StepConfig[] = [
  {
    name: 'Scenario',
    goTo: '/scenario_step_wizard',
    icon: 'configuration',
    key: 'wizard',
    fields: [
      'name',
      'totalEnergiesShareholdingInterest',
      'durationYear',
      'irc',
      'hasBattery',
      'hasGenset',
      'hasGrid',
      'configuration',
      'hasInjection',
      'injectionModelRef',
      'businessModelRef',
      'grid.hasGrid24',
      'analyticalCode',
      'startDate',
      'constructionDuration',
      'exploitationDuration',
      'technology',
      'region',
      'metropoleRegion'
    ]
  },
  {
    name: 'Site',
    goTo: '/scenario_step_load',
    icon: 'load_03',
    key: 'load',
    fields: ['load']
  },
  {
    name: 'PV',
    goTo: '/scenario_step_pv',
    icon: 'pv_farm',
    key: 'pv',
    fields: [
      'pv',
      'eco.hasBreakdownCapex',
      'eco.epcProposalFixed',
      'eco.epcProposalVariable',
      'eco.breakdown.pvModule',
      'eco.breakdown.pvRackSystem',
      'eco.breakdown.pvInverter',
      'eco.breakdown.pvInverterAcdcRatio',
      'eco.breakdown.pvLogisticAndTransport',
      'eco.breakdown.otherAcBillOfService',
      'eco.breakdown.otherDcBillOfService',
      'eco.breakdown.otherLogisticAndTransport',
      'eco.breakdown.otherAntiInjectionDevice',
      'eco.breakdown.pvStructureCivilWorks',
      'eco.breakdown.pvStructureShelter',
      'eco.breakdown.projectInstallationPriceFixed',
      'eco.breakdown.pvMechanicalInstallation',
      'eco.breakdown.pvElectricalInstallation',
      'eco.breakdown.projectEngineeringAndAdministrative',
      'eco.breakdown.projectAffiliatesAndHq',
      'eco.breakdown.projectSupervision',
      'eco.breakdown.projectEpcMargin',
      'eco.future.pvInverter',
      'eco.future.pvInverterAcdcRatio',
      'eco.om.pvCost',
      'eco.om.pvFixedCost'
    ]
  },
  {
    name: 'Grid',
    goTo: '/scenario_step_grid',
    icon: 'grid',
    key: 'grid',
    fields: [
      'grid.hasDifferentAvailabilityAfter',
      'grid.averageGridConnectionHoursPerDayBefore',
      'grid.averageGridConnectionHoursPerDayBeforePercentage',
      'grid.averageGridConnectionHoursPerDayAfter',
      'grid.averageGridConnectionHoursPerDayAfterPercentage',
      'grid.calculatedCurveBefore',
      'grid.calculatedCurveAfter',
      'grid.hasGridLimitation',
      'grid.limitPower',
      'grid.limitPercentage',
      'grid.availabilityTypeBefore',
      'grid.gridComplexAvailabilityBefore',
      'grid.availabilityTypeAfter',
      'grid.gridComplexAvailabilityAfter',
      'eco.gridComplexPricing',
      'eco.gridFixFee',
      'eco.gridPrice',
      'eco.gridMyFilePricing',
      'eco.gridPriceEscalationRate',
      'eco.gridFixFeeEscalationRate',
      'eco.gridPricingTypeRef',
      'eco.gridFixFeeTax',
      'eco.gridPriceTax'
    ]
  },
  {
    name: 'Generator',
    goTo: '/scenario_step_generator',
    icon: 'genset',
    key: 'generator',
    fields: [
      'generator',
      'eco.fuelPrice',
      'eco.fuelEscalationRate',
      'eco.fuelPriceType1',
      'eco.fuelEscalationRateType1',
      'eco.fuelPriceType2',
      'eco.fuelEscalationRateType2',
      'eco.breakdown.generatorBeforeDayCost',
      'eco.breakdown.generatorBeforeNightCost',
      'eco.breakdown.generatorBeforeLogisticAndTransport',
      'eco.breakdown.generatorAfterDayCost',
      'eco.breakdown.generatorAfterNightCost',
      'eco.breakdown.generatorAfterLogisticAndTransport',
      'eco.future.generatorBeforeDayCost',
      'eco.future.generatorBeforeNightCost',
      'eco.future.generatorAfterDayCost',
      'eco.future.generatorAfterNightCost',
      'eco.om.generatorFixedCost',
      'eco.om.generatorVariableCost'
    ],
    excludedFields: [
      'generator.optimumPercentage'
    ]
  },
  {
    name: 'Battery',
    goTo: '/scenario_step_battery',
    icon: 'battery',
    key: 'battery',
    fields: [
      'battery.socMinPercentage',
      'battery.globalSocMaxPercentage',
      'battery.isGridForming',
      'battery.typeRef',
      'battery.cRate',
      'battery.chargeEfficiencyPercentage',
      'battery.dischargeEfficiencyPercentage',
      'battery.lifetimeCycles',
      'battery.invertersLifetimeYear',
      'battery.fromHour',
      'battery.toHour',
      'eco.om.batteryCost',
      'eco.breakdown.batteryModule',
      'eco.breakdown.batteryModule',
      'eco.breakdown.batteryPriceReduction',
      'eco.breakdown.batteryInverter',
      'eco.breakdown.batteryLogisticAndTransport',
      'eco.breakdown.batteryStructureCivilWorks',
      'eco.breakdown.batteryStructureShelter',
      'eco.breakdown.batteryMechanicalInstallation',
      'eco.breakdown.batteryElectricalInstallation',
      'eco.future.batteryModule',
      'eco.future.batteryPriceReduction',
      'eco.future.batteryInverter'
    ]
  },
  {
    name: 'Eco',
    goTo: '/scenario_step_eco',
    icon: 'mdi-currency-usd',
    key: 'eco',
    fields: [
      'eco.feedIn.feedInPricingTypeRef',
      'eco.feedIn.hasPaymentForExcessAnnualGeneration',
      'eco.feedIn.feedInDuration',
      'eco.feedIn.feedInTariff',
      'eco.feedIn.feedInTariffEscalationRate',
      'eco.feedIn.feedInTariff2',
      'eco.feedIn.feedInTariffEscalationRate2',
      'eco.feedIn.feedInFlexibleTariff',
      'eco.feedIn.feedInPPAvsMerchant',
      'eco.feedIn.feedInMerchantPriceVector',
      'eco.feedIn.routeToMarket',
      'eco.useAffiliateDebt',
      'eco.useFarmdown',
      'eco.additionalCashFlow',
      'eco.fixedYearlyCashFlowOtherCost',
      'eco.fixedYearlyCashFlowOtherCostYearsAffected',
      'eco.totalAmountOverConstruction',
      'eco.hasTaxCredit',
      'eco.taxCreditRate',
      'eco.taxCreditYear',
      'eco.revenues.revenuesTypeRef',
      'eco.revenues.ppaTakeOrPay',
      'eco.revenues.minimumTakeOff',
      'eco.revenues.contractDuration',
      'eco.revenues.proportionOfSavings',
      'eco.corporateTaxRate',
      'eco.discountRate',
      'eco.currency',
      'eco.projectFinancingStructureTypeRef',
      'eco.lossCarryForwardLimit',
      'eco.withholdingTaxOnDividends',
      'eco.taxHoliday',
      'eco.depreciationTypeRef',
      'eco.depreciationTime',
      'eco.depreciationPercentage',
      'eco.affiliateGearing',
      'eco.minimumDscr',
      'eco.tenor',
      'eco.shortTermInterestRate',
      'eco.revenues.solarMonthlyFeeValue',
      'eco.revenues.solarMonthlyFeePercentage',
      'eco.revenues.solarMonthlyFeeYearlyInflation',
      'eco.gensetTaxes',
      'eco.shelterTaxes',
      'eco.pvModuleTaxes',
      'eco.pvRackingTaxes',
      'eco.pvInverterTaxes',
      'eco.batteryTaxes',
      'eco.installationTaxes',
      'eco.exchangeRate',
      'eco.inflationRateLocalCurrency',
      'eco.inflationRateUSD',
      'eco.revenues.ppaPrice',
      'eco.revenues.ppaYearlyInflation'
    ]
  },
  {
    name: 'Sizing',
    goTo: '/scenario_step_sizing',
    icon: 'trending_up',
    key: 'sizing',
    fields: ['sizing',
      'battery.isRechargeableByGrid',
      'battery.socMaxChargeGrid',
      'battery.isRechargeableByGenerator',
      'battery.socMaxChargeGen',
      'generator.optimumPercentage']
  },
  {
    name: 'Technical results',
    goTo: '/scenario_technical_report',
    icon: 'mdi-apps',
    key: 'tech_report',
    fields: []
  },
  {
    name: 'Economical results',
    goTo: '/scenario_economical_report',
    icon: 'mdi-apps',
    key: 'eco_report',
    fields: []
  },
  {
    name: 'Client results',
    goTo: '/scenario_client_result',
    icon: 'mdi-apps',
    key: 'client_result',
    fields: []
  },
  {
    name: 'Executive report',
    goTo: '/scenario_executive_report',
    icon: 'mdi-apps',
    key: 'executive_report',
    fields: []
  },
  // for ren_fr project
  {
    name: 'Ren_fr pv',
    goTo: '/scenario_ren_fr_step_pv',
    icon: 'pv_farm',
    key: 'ren_fr_pv',
    fields: [
      'ren_fr_pv.unit_power',
      'ren_fr_pv.module_area',
      'ren_fr_pv.p50_annual_degradation',
      'ren_fr_pv.p90_annual_degradation',
      'ren_fr_pv.installed_capacity',
      'ren_fr_pv.dc_ac_ratio',
      'ren_fr_pv.net_p50',
      'ren_fr_pv.net_p90',
      'ren_fr_pv.numberOfPdl',
      'ren_fr_pv.numberOfPdlPtr',
      'ren_fr_pv.numberOfPtr'
    ]
  },
  {
    name: 'Ren_fr wind',
    goTo: '/scenario_ren_fr_step_wind',
    icon: 'mdi-wind-power',
    key: 'ren_fr_wind',
    fields: [
      'ren_fr_wind.numberOfTurbineType1',
      'ren_fr_wind.turbineModelType1',
      'ren_fr_wind.unitPowerOfTurbineType1',
      'ren_fr_wind.rotorDiameterType1',
      'ren_fr_wind.numberOfTurbineType2',
      'ren_fr_wind.turbineModelType2',
      'ren_fr_wind.unitPowerOfTurbineType2',
      'ren_fr_wind.rotorDiameterType2',
      'ren_fr_wind.net_p50',
      'ren_fr_wind.net_p90',
      'ren_fr_wind.numberOfPdl',
      'ren_fr_wind.numberOfPdlPtr',
      'ren_fr_wind.numberOfPtr'
    ]
  },
  {
    name: 'Revenue',
    goTo: '/scenario_ren_fr_step_revenue',
    icon: 'mdi-finance',
    key: 'revenue',
    fields: [
      'revenue.contractType',
      'revenue.caseType',
      'revenue.ppa1Duration',
      'revenue.ppa1RateInflationIndex',
      'revenue.ppa1ProductionSold',
      'revenue.leaseDuration',
      'revenue.selfConsumptionPlantRent',
      'revenue.leaseInflation'
    ]
  },
  {
    name: 'Capex',
    goTo: '/scenario_ren_fr_step_capex',
    icon: 'mdi-alpha-c-circle-outline',
    key: 'capex',
    fields: [
      'capex.spvPurchaseDate',
      'capex.spvPurchase',
      'capex.impactStudy',
      'capex.publicInquiry',
      'capex.permitDisplay',
      'capex.technical',
      'capex.businessDeveloper',
      'capex.archaeologicalAssessment',
      'capex.lawyerFees',
      'capex.assetRent',
      'capex.devContractRenFr',
      'capex.devContractExternal',
      'capex.amoContract',
      'capex.others',
      'capex.productible',
      'capex.authorizations',
      'capex.gridConnection',
      'capex.specializedLink',
      'capex.gridConnectionCost',
      'capex.regionShare',
      'capex.telecom',
      'capex.environmentalMonitoring',
      'capex.ercMeasures',
      'capex.lot0_CspsCt',
      'capex.lot1_PublicWorks',
      'capex.lot2_Foundations',
      'capex.lot3_ElectricalNetwork',
      'capex.lot4_PdlPtr',
      'capex.lot5_WindTurbines',
      'capex.lot6_Supervision',
      'capex.lot7_Ess',
      'capex.lot8_Modules',
      'capex.lot9_Decommissioning',
      'capex.lot10_Transport',
      'capex.misc',
      'capex.uncertainties'
    ]
  },
  {
    name: 'Opex',
    goTo: '/scenario_ren_fr_step_opex',
    icon: 'mdi-alpha-o-circle-outline',
    key: 'opex',
    fields: [
      'opex.actualAdministrativeManagementCost',

      'opex.leaseArea',

      'opex.fixedAnnualRentPeriod1',

      'opex.variableAnnualRentPeriod1',

      'opex.purchasePriceOfLand',
      'opex.upfrontRentPayment',

      'opex.omContractWithMargin',
      'opex.omContractWithoutMargin',
      'opex.verifications',
      'opex.networkMeterDeie',
      'opex.electricity',
      'opex.telecom',
      'opex.maintenanceRepairsForRealEstateEquipmentWorks',

      'opex.environmentalChargesYear1',
      'opex.environmentalChargesYear2',
      'opex.environmentalChargesYear3',
      'opex.environmentalChargesYear4',
      'opex.environmentalChargesYear5',
      'opex.environmentalChargesYear6',
      'opex.environmentalChargesYear7',
      'opex.environmentalChargesYear8',
      'opex.environmentalChargesYear9',
      'opex.environmentalChargesYear10',
      'opex.environmentalChargesYear11',
      'opex.environmentalChargesYear12',
      'opex.environmentalChargesYear13',
      'opex.environmentalChargesYear14',
      'opex.environmentalChargesYear15',
      'opex.environmentalChargesYear16',
      'opex.environmentalChargesYear17',
      'opex.environmentalChargesYear18',
      'opex.environmentalChargesYear19',
      'opex.environmentalChargesYear20',
      'opex.environmentalChargesYear21',
      'opex.environmentalChargesYear22',
      'opex.environmentalChargesYear23',
      'opex.environmentalChargesYear24',
      'opex.environmentalChargesYear25',
      'opex.environmentalChargesYear26',
      'opex.environmentalChargesYear27',
      'opex.environmentalChargesYear28',
      'opex.environmentalChargesYear29',
      'opex.environmentalChargesYear30',
      'opex.environmentalChargesYear31',
      'opex.environmentalChargesYear32',
      'opex.environmentalChargesYear33',
      'opex.environmentalChargesYear34',
      'opex.environmentalChargesYear35',

      'opex.otherChargesYear1',
      'opex.otherChargesYear2',
      'opex.otherChargesYear3',
      'opex.otherChargesYear4',
      'opex.otherChargesYear5',
      'opex.otherChargesYear6',
      'opex.otherChargesYear7',
      'opex.otherChargesYear8',
      'opex.otherChargesYear9',
      'opex.otherChargesYear10',
      'opex.otherChargesYear11',
      'opex.otherChargesYear12',
      'opex.otherChargesYear13',
      'opex.otherChargesYear14',
      'opex.otherChargesYear15',
      'opex.otherChargesYear16',
      'opex.otherChargesYear17',
      'opex.otherChargesYear18',
      'opex.otherChargesYear19',
      'opex.otherChargesYear20',
      'opex.otherChargesYear21',
      'opex.otherChargesYear22',
      'opex.otherChargesYear23',
      'opex.otherChargesYear24',
      'opex.otherChargesYear25',
      'opex.otherChargesYear26',
      'opex.otherChargesYear27',
      'opex.otherChargesYear28',
      'opex.otherChargesYear29',
      'opex.otherChargesYear30',
      'opex.otherChargesYear31',
      'opex.otherChargesYear32',
      'opex.otherChargesYear33',
      'opex.otherChargesYear34',
      'opex.otherChargesYear35',

      'opex.omTurbineDurationPeriod1',
      'opex.omTurbineDurationPeriod2',
      'opex.omTurbineDurationPeriod3',
      'opex.omTurbineDurationPeriod4',
      'opex.omTurbineDurationPeriod5',
      'opex.omTurbineDurationPeriod6',

      'opex.omTurbineFixedPricePeriod1',
      'opex.omTurbineFixedPricePeriod2',
      'opex.omTurbineFixedPricePeriod3',
      'opex.omTurbineFixedPricePeriod4',
      'opex.omTurbineFixedPricePeriod5',
      'opex.omTurbineFixedPricePeriod6',

      'opex.omTurbineVariablePricePeriod1',
      'opex.omTurbineVariablePricePeriod2',
      'opex.omTurbineVariablePricePeriod3',
      'opex.omTurbineVariablePricePeriod4',
      'opex.omTurbineVariablePricePeriod5',
      'opex.omTurbineVariablePricePeriod6',

      'opex.omTurbineMinimumVariablePricePeriod1',
      'opex.omTurbineMinimumVariablePricePeriod2',
      'opex.omTurbineMinimumVariablePricePeriod3',
      'opex.omTurbineMinimumVariablePricePeriod4',
      'opex.omTurbineMinimumVariablePricePeriod5',
      'opex.omTurbineMinimumVariablePricePeriod6',

      'opex.municipalRate',
      'opex.departmentalRate',

      'opex.municipalRateCFE',
      'opex.interMunicipalRateCFE',
      'opex.coefficientDeNeutralisationCFE',

      'opex.municipalRatePropertyTax',
      'opex.interMunicipalRatePropertyTax',
      'opex.coefficientDeNeutralisationTF'
    ]
  },
  {
    name: 'Structure Finance',
    goTo: '/scenario_ren_fr_step_structure_finance',
    icon: 'mdi-currency-eur',
    key: 'structure_finance',
    fields: [
      'structure_finance.crowdfunding',
      'structure_finance.linearDepreciationParticipative',
      'structure_finance.percentageOfCapex',
      'structure_finance.coShareholder1',
      'structure_finance.percentageOwnershipCoShareholder1',
      'structure_finance.coShareholder2',
      'structure_finance.percentageOwnershipCoShareholder2',
      'structure_finance.perRenSoldFarmDown'
    ]
  },
  {
    name: 'Ren_fr Economical results',
    goTo: '/scenario_ren_fr_economical_report',
    icon: 'mdi-apps',
    key: 'ren_fr_eco_report',
    fields: []
  }
]
  .map((c, i) => ({
    ...c,
    index: i,
    available: true
  }))
  .map(c => new StepConfig(c));

/**
  * Errors that define sizing step access
*/
// const excludedErrorsForSizingValidation = [
//   'battery.isRechargeableByGrid', 'battery.isRechargeableByGenerator', 'battery.socMaxChargeGrid',
//   'battery.socMaxChargeGen',
//   'generator.generatorBeforeDay.optimumPercentage',
//   'generator.generatorBeforeNight.optimumPercentage',
//   'generator.generatorAfterDay.optimumPercentage',
//   'generator.generatorAfterNight.optimumPercentage',
//   'generator.generatorAfterType1.optimumPercentage',
//   'generator.generatorAfterType2.optimumPercentage'
// ];

/**
 * Debug 'Dev' step is added in dev environment only
 */
if (envHelper.inDevOnly()) {
  allSteps.push(
    new StepConfig({
      name: 'Dev',
      goTo: '/scenario_step_dev',
      icon: 'adb',
      key: 'dev',
      fields: ['dev'],
      index: allSteps.length,
      available: true,
      color: 'grey'
    })
  );
}

const stepsBeforeSizing = ['wizard', 'load', 'pv', 'grid', 'generator', 'battery', 'eco', 'ren_fr_pv', 'ren_fr_wind', 'revenue', 'capex', 'opex'];

export default {
  allSteps,
  stepsBeforeSizing
  // excludedErrorsForSizingValidation
};
